import { buildQueryParams, type ResponseMeta, type StrapiQueryParams } from "@investhinks/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useStrapiFilters, { type StrapiFilters } from "./useStrapiFilters";
import useStrapiPagination, { type PaginationState } from "./useStrapiPagination";
import useStrapiSort, { type SortDirection, type StrapiSort } from "./useStrapiSort";

export type QueryParams = StrapiQueryParams & Record<string, unknown>;

// Type for API responses - accepts any Promise-returning function
// The function returns { data: T[], meta: { pagination: {...} } } format
// Note: Using Record<string, unknown> for params due to contravariance - callbacks with
// specific param types need a param type that accepts any object shape
type ApiListLoadFn = (params: Record<string, unknown>) => Promise<unknown>;

// Type guard to check if result is { data, meta } format from api-client
function isApiResponse(result: unknown): result is { data: unknown[]; meta?: ResponseMeta } {
  if (result === null || typeof result !== "object" || !("data" in result)) return false;
  // After the "data" in result check, TS narrows result to have a data property
  const dataValue: unknown = result.data;
  return Array.isArray(dataValue);
}

/**
 * Crosses the unknown -> T type boundary for API responses.
 * API data is validated by Zod schemas in the api-client layer before reaching this hook,
 * so the runtime type is guaranteed correct. This function exists to cross the type system
 * boundary in a single documented location rather than using inline casts.
 */
function asTypedArray<T>(arr: unknown[]): T[] {
  return arr as T[];
}

export interface UseStrapiListOptions {
  requestKey?: string;
  load?: ApiListLoadFn;
  onLoadError?: (error: unknown) => void;
  autoLoad?: boolean;
  filters?: StrapiFilters;
  /** Transform filters before sending to API (e.g., convert 'search' to $or conditions) */
  transformFilters?: (filters: StrapiFilters) => StrapiFilters | undefined;
  pagination?: Partial<PaginationState>;
  sort?: StrapiSort;
  populate?: string | string[] | Record<string, unknown>;
  depsToReset?: unknown[];
  publicationState?: "live" | "preview";
  /**
   * Extra query params merged into every request. For endpoints that take
   * their own top-level params instead of reading the generic `filters`
   * object.
   */
  extraParams?: Record<string, unknown>;
}

export interface UseStrapiListReturn<T = unknown> {
  // Items
  items: T[];
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  isLoading: boolean;
  error: Error | null;
  hasLoaded: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refresh: () => Promise<T[] | undefined>;
  reset: () => void;

  // Pagination
  pagination: PaginationState;
  setPagination: (pagination: Partial<PaginationState> | ((prev: PaginationState) => Partial<PaginationState>)) => void;
  goToPage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  resetPagination: () => void;

  // Sort
  sort: StrapiSort;
  setSort: React.Dispatch<React.SetStateAction<StrapiSort>>;
  changeSort: (field: string | StrapiSort, direction?: SortDirection) => void;
  clearSort: () => void;

  // Filters
  filters: StrapiFilters;
  setFilters: (filters: StrapiFilters | ((prev: StrapiFilters) => StrapiFilters)) => void;
  changeFilter: (key: string, value: unknown) => void;
  changeFilters: (key: string, value: unknown) => void;
  resetFilters: () => void;

  // Query params (for debugging/inspection)
  queryParams: QueryParams;
}

/**
 * Hook for fetching and managing lists of items from Strapi API
 */
function useStrapiList<T = unknown>(options: UseStrapiListOptions = {}): UseStrapiListReturn<T> {
  const {
    load,
    onLoadError,
    autoLoad = true,
    filters: initialFilters = {},
    transformFilters,
    pagination: initialPagination = {},
    sort: initialSort = {},
    populate,
    depsToReset = [],
    publicationState,
    extraParams,
    requestKey,
  } = options;

  const [items, setItems] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(autoLoad);
  const [error, setError] = useState<Error | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const initialLoadDone = useRef(false);
  // Track the depsToReset value that was active during last successful load
  // This prevents resetting on first load when deps transition from undefined to valid
  const lastLoadedDepsRef = useRef<string | null>(null);
  // Counter to skip auto-load when depsToReset triggers (incremented on reset, decremented on skip)
  const skipAutoLoadCountRef = useRef(0);

  // Compose sub-hooks
  const { pagination, setPagination, goToPage, changePageSize, resetPagination, updateFromResponse, paginationParams } =
    useStrapiPagination(initialPagination);

  const { sort, setSort, changeSort, clearSort, sortParams } = useStrapiSort(initialSort);

  const { filters, setFilters, changeFilter, resetFilters, filterParams } = useStrapiFilters(initialFilters);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Build query params object (structured, not flattened)
  // Serialized rather than compared by identity: callers pass an inline object,
  // so identity changes every render while the value usually does not. Declared
  // before getQueryParams because that dep array references it.
  const serializedExtraParams = useMemo(() => JSON.stringify(extraParams ?? null), [extraParams]);

  const getQueryParams = useCallback((): QueryParams => {
    // Apply transformFilters if provided (e.g., to convert 'search' to $or conditions)
    const finalFilters = transformFilters && filterParams ? transformFilters(filterParams) : filterParams;

    // Object.assign adds index signature so StrapiQueryParams becomes QueryParams
    const built = Object.assign<Record<string, unknown>, StrapiQueryParams>(
      {},
      buildQueryParams({
        filters: finalFilters,
        pagination: paginationParams,
        sort: sortParams,
        populate,
        publicationState,
      })
    );
    return extraParams ? Object.assign(built, extraParams) : built;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams, transformFilters, paginationParams, sortParams, populate, publicationState, serializedExtraParams]);

  // Serialize params for dependency comparison (avoids complex expressions in deps)
  const serializedFilterParams = useMemo(() => JSON.stringify(filterParams), [filterParams]);
  const serializedPaginationParams = useMemo(() => JSON.stringify(paginationParams), [paginationParams]);
  const serializedSortParams = useMemo(() => JSON.stringify(sortParams), [sortParams]);
  const serializedPopulate = useMemo(() => JSON.stringify(populate), [populate]);
  const serializedDepsToReset = useMemo(() => JSON.stringify(depsToReset), [depsToReset]);

  // Invalidate old responses immediately when navigating or changing a query.
  const queryKey = JSON.stringify([requestKey, serializedFilterParams, serializedPaginationParams,
    serializedSortParams, serializedPopulate, publicationState, serializedDepsToReset, serializedExtraParams]);
  const previousQueryKey = useRef(queryKey);
  if (previousQueryKey.current !== queryKey) {
    previousQueryKey.current = queryKey;
    requestIdRef.current += 1;
    setItems([]);
    setHasLoaded(false);
    setError(null);
    setIsLoading(autoLoad);
  }

  // Fetch data
  const refresh = useCallback(async (): Promise<T[] | undefined> => {
    if (!load) return undefined;

    const requestId = ++requestIdRef.current;
    const isCurrent = () => mountedRef.current && requestId === requestIdRef.current;
    setIsLoading(true);
    try {
      const params = getQueryParams();
      const result = await load(params);

      if (!isCurrent()) return undefined;

      // API returns { data: T[], meta: { pagination: {...} } } format
      if (isApiResponse(result)) {
        const typedData = asTypedArray<T>(result.data);
        setItems(typedData);
        setHasLoaded(true);
        setError(null);
        if (result.meta) {
          updateFromResponse(result.meta);
        }
        return typedData;
      } else if (Array.isArray(result)) {
        // Fallback for direct array responses (legacy or custom endpoints)
        const typedResult = asTypedArray<T>(result);
        setItems(typedResult);
        setHasLoaded(true);
        setError(null);
        return typedResult;
      } else {
        throw new Error("Invalid list response");
      }
    } catch (error: unknown) {
      if (!isCurrent()) return undefined;
      setError(error instanceof Error ? error : new Error(String(error)));
      if (onLoadError) {
        onLoadError(error);
      } else {
        console.error("useStrapiList load error:", error);
      }
      return undefined;
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  }, [load, getQueryParams, updateFromResponse, onLoadError]);

  // Keep refresh in a ref to avoid it being a dependency in the auto-load effect
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  // Reset pagination/sort when depsToReset change (for navigating between items)
  // Only reset if we've previously loaded with different deps (not first load)
  useEffect(() => {
    const prevLoadedDeps = lastLoadedDepsRef.current;
    const shouldReset = depsToReset.length > 0 && prevLoadedDeps !== null && prevLoadedDeps !== serializedDepsToReset;

    if (shouldReset) {
      // Increment skip counter - auto-load should wait for resets to take effect
      skipAutoLoadCountRef.current += 1;
      resetPagination();
      resetFilters();
      clearSort();
      // Update lastLoadedDeps now so we don't reset again
      lastLoadedDepsRef.current = serializedDepsToReset;
      // Note: Resets change state, which triggers re-render, auto-load runs with reset params
    }
  }, [serializedDepsToReset, depsToReset.length, resetPagination, resetFilters, clearSort]);

  // Auto-load on mount and when params change
  useEffect(() => {
    // Skip if we're waiting for resets to take effect (depsToReset triggered)
    // The resets will cause a re-render, and auto-load will run then with reset params
    if (skipAutoLoadCountRef.current > 0) {
      skipAutoLoadCountRef.current -= 1;
      return;
    }

    if (autoLoad) {
      refreshRef.current();
      initialLoadDone.current = true;
      // Track what deps we loaded with (for depsToReset logic)
      lastLoadedDepsRef.current = serializedDepsToReset;
    }
  }, [
    autoLoad,
    serializedFilterParams,
    serializedPaginationParams,
    serializedSortParams,
    serializedPopulate,
    publicationState,
    serializedDepsToReset,
    serializedExtraParams,
    requestKey,
  ]);

  // Full reset
  const reset = useCallback(() => {
    resetPagination();
    resetFilters();
    clearSort();
    setItems([]);
  }, [resetPagination, resetFilters, clearSort]);

  return {
    // Items
    items,
    setItems,
    isLoading,
    error,
    hasLoaded,
    setLoading: setIsLoading,
    refresh,
    reset,

    // Pagination
    pagination,
    setPagination,
    goToPage,
    changePageSize,
    resetPagination,

    // Sort
    sort,
    setSort,
    changeSort,
    clearSort,

    // Filters
    filters,
    setFilters,
    changeFilter,
    changeFilters: changeFilter, // Alias for backwards compatibility
    resetFilters,

    // Query params (for debugging/inspection)
    queryParams: getQueryParams(),
  };
}

export default useStrapiList;
